import { FrontendProduct } from "@redotech/redo-model/agents/support-agents/frontend-product";
import { ProductDisplayMessageType } from "@redotech/redo-model/conversation/message-parsers/product-display-message-parser";
import { memo, useMemo } from "react";
import { Flex } from "../flex";
import {
  ProductByIdFetcher,
  useProducts,
} from "../order-editing/hooks/use-products";
import { ProductCarousel } from "../product/product-carousel";
import { ProductTileProps } from "../product/product-tile";
import { Text } from "../text";

interface ProductIdType {
  productId: string;
}

interface ProductDisplayCarouselWrapperProps {
  message: ProductDisplayMessageType;
  teamId?: string;
  fetcher?: ProductByIdFetcher;
  productTileProps: Omit<ProductTileProps, "product" | "topRightButton"> & {
    topRightButton?: (product: FrontendProduct) => React.ReactNode;
  };
}

export const ProductDisplayCarouselWrapper = memo(
  function ProductDisplayCarouselWrapper({
    message,
    teamId,
    fetcher,
    productTileProps,
  }: ProductDisplayCarouselWrapperProps) {
    const productIds = useMemo(() => {
      return (
        message.structuredContent?.productsToDisplay?.map(
          (item: ProductIdType) => item.productId,
        ) || []
      );
    }, [message.structuredContent?.productsToDisplay]);

    const { productsMap, isLoading, error } = useProducts(
      teamId,
      fetcher,
      productIds,
    );

    const products = useMemo(() => {
      return Array.from(productsMap.values()).filter(
        (product) =>
          product.images?.[0]?.url ||
          product.variants.some((v) => v.images?.[0]?.url),
      );
    }, [productsMap]);

    // Only show error if we have no products and there's an error and not loading
    // This prevents showing stale errors after products have loaded successfully
    const shouldShowError = error && products.length === 0 && !isLoading;

    if (!teamId || !fetcher) {
      return (
        <Flex justify="center" p="lg">
          <Text color="error">Product display not configured</Text>
        </Flex>
      );
    }

    return (
      <ProductCarousel
        error={shouldShowError ? error.message : undefined}
        loading={isLoading}
        products={products}
        productTileProps={productTileProps}
      />
    );
  },
);
